import { useMemo, useState } from "react"
import { v4 } from "uuid"
import * as Icons from "@nova/component-icons"
import { ButtonDestructive } from "@nova/component-btn"
import { FileExtension } from "@nova/constants"
import { PdfLogoSettings } from "@nova/schemas"
import { useTranslation } from "@nova/use-translation"
import { useAppSelector } from "integrations/redux/hooks"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { UploadDropBox } from "panels/content/side-panel/upload-flow/components/UploadDropBox"
import {
    FileUploadCategory,
    mapExtensionsToMimeType,
    supportedFileUploadExtensions,
} from "features/job-management/uploader/constants"
import { SegmentedControl } from "panels/content/top-bar/TODO-put-in-package/SegmentedControl"
import { uploadWorkspaceLogoToS3 } from "plugins/shared/uploadPDFLogoToS3"
import {
    PDF_NONE_LOGO_VALUE,
    parsePdfLogoSettings,
    resolvePdfLogoSrc,
} from "plugins/shared/pdfLogos"

type Slot = "leftLogo" | "rightLogo"

export const PdfLogoManager = ({
    name,
    value,
    onChange,
}: {
    name: string
    value: string
    onChange: (name: string, value: string) => void
}) => {
    const { t } = useTranslation()
    const { isEditor } = useWorkspacePermissions()
    const orgId = useAppSelector(selectActiveWorkspaceId())
    const [previewTheme, setPreviewTheme] = useState<"light" | "dark">("light")
    const [uploadingSlot, setUploadingSlot] = useState<Slot | null>(null)

    const settings = useMemo(() => parsePdfLogoSettings(value), [value])
    const commit = (next: PdfLogoSettings) => onChange(name, JSON.stringify(next))

    const handleUpload = (slot: Slot) => async (files: File[]) => {
        const file = files[0]
        if (!file || !orgId) return true
        setUploadingSlot(slot)
        try {
            const id = v4()
            await uploadWorkspaceLogoToS3(orgId, id, file)
            commit({ ...settings, [slot]: { id, name: file.name } })
        } finally {
            setUploadingSlot(null)
        }
        return true
    }

    // Store the explicit "none" sentinel so a cleared slot stays empty — distinct from
    // an unconfigured slot, where the left logo falls back to the Nova logo.
    const handleDelete = (slot: Slot) =>
        commit({ ...settings, [slot]: { id: PDF_NONE_LOGO_VALUE, name: "" } })

    // Resolves a slot to the preview image src, or null when the slot is empty.
    const resolveSlotSrc = (slot: Slot): string | null => {
        const side = slot === "leftLogo" ? "left" : "right"
        const src = resolvePdfLogoSrc(settings[slot], side, orgId ?? "")
        return src === "" ? null : src
    }

    const renderCard = (slot: Slot, label: string) => {
        const src = resolveSlotSrc(slot)

        if (!isEditor && !src) return null

        return (
            <div className="flex w-[220px] flex-col gap-2">
                <small className="text-gray-100">{label}</small>
                {src ? (
                    <div className="flex h-[192px] w-[220px] flex-col">
                        <div
                            className={`flex-1 min-h-0 w-full flex flex-col items-center justify-center overflow-hidden rounded-lg ${
                                previewTheme === "light" ? "bg-white" : "bg-gray-900"
                            }`}
                        >
                            <img
                                src={src}
                                alt={label}
                                width={180}
                                height={140}
                                className="max-h-[160px] max-w-[200px] w-auto object-contain"
                            />
                        </div>
                        {isEditor && (
                            <ButtonDestructive
                                className="mt-2"
                                LeftIcon={Icons.Trash}
                                onClick={() => handleDelete(slot)}
                            />
                        )}
                    </div>
                ) : (
                    <div className="flex h-[192px] w-[220px]">
                        <UploadDropBox
                            allowedFileTypes={[
                                FileExtension.png,
                                FileExtension.jpg,
                                FileExtension.jpeg,
                            ]}
                            title="Upload Logo"
                            onNewItems={handleUpload(slot)}
                            isProcessing={uploadingSlot === slot}
                            width="100%"
                            supportedMimeTypes={supportedFileUploadExtensions[
                                FileUploadCategory.Logo
                            ].map((extension) => mapExtensionsToMimeType[extension])}
                        />
                    </div>
                )}
            </div>
        )
    }

    const hasPreview = resolveSlotSrc("leftLogo") !== null || resolveSlotSrc("rightLogo") !== null

    return (
        <div className="flex gap-12 pb-5">
            <div className="w-[12.5rem]">
                <h5 className="text-gray-100 mb-2">{t("PDF Default Logos")}</h5>
                {hasPreview && (
                    <div className="w-[11.25rem]">
                        <SegmentedControl
                            segments={[
                                <span
                                    key="light"
                                    className="flex items-center justify-center gap-1"
                                >
                                    <Icons.Sun /> {t("Light")}
                                </span>,
                                <span key="dark" className="flex items-center justify-center gap-1">
                                    <Icons.Moon /> {t("Dark")}
                                </span>,
                            ]}
                            defaultSelectedIndex={previewTheme === "light" ? 0 : 1}
                            onChange={(index) => setPreviewTheme(index === 0 ? "light" : "dark")}
                        />
                    </div>
                )}
            </div>
            <div className="flex flex-wrap gap-4">
                {renderCard("leftLogo", t("Left Logo"))}
                {renderCard("rightLogo", t("Right Logo"))}
            </div>
        </div>
    )
}
